/**
 * Games Module - Orchestrates game rendering and interactions in chat
 */
import { GamesAPI } from '../services/api.js';
import { sendGameInvite, sendGameAction, requestGameState, isConnected } from '../services/socket.js';
import { AppState } from '../config.js';
import { showToast, Utils } from '../utils/ui.js';
import { t } from '../utils/i18n.js';
import { pushModalState, removeModalState } from '../utils/history.js';

import * as thisOrThatRenderer from '../games/thisOrThat.js';
import * as wouldYouRatherRenderer from '../games/wouldYouRather.js';
import * as truthOrDareRenderer from '../games/truthOrDare.js';

const renderers = {
    this_or_that: thisOrThatRenderer,
    would_you_rather: wouldYouRatherRenderer,
    truth_or_dare: truthOrDareRenderer
};

const lang = () => document.documentElement.lang === 'he' ? 'he' : 'en';

// Track active game sessions in current chat
let activeGameSessionId = null;

/**
 * Render the game picker bottom sheet
 */
export function renderGamePicker(matchId) {
    const games = [
        { key: 'this_or_that', emoji: '⚡', name: t('games.thisOrThat'), desc: t('games.thisOrThatDesc'), rounds: 5 },
        { key: 'would_you_rather', emoji: '🤔', name: t('games.wouldYouRather'), desc: t('games.wouldYouRatherDesc'), rounds: 5 },
        { key: 'truth_or_dare', emoji: '🎯', name: t('games.truthOrDare'), desc: t('games.truthOrDareDesc'), rounds: 6 }
    ];

    const pickerEl = document.getElementById('game-picker-sheet');
    if (!pickerEl) return;

    pickerEl.innerHTML = `
        <div class="game-picker-overlay" id="game-picker-overlay"></div>
        <div class="game-picker-content" style="animation: slideUpSheet 0.3s ease-out">
            <div class="flex items-center justify-between mb-4 px-1">
                <h3 class="text-lg font-bold text-gray-900 dark:text-white">${t('games.chooseGame')}</h3>
                <button id="close-game-picker" class="text-gray-400 hover:text-gray-600 p-1">
                    <i class="fas fa-times"></i>
                </button>
            </div>
            <div class="space-y-3">
                ${games.map(g => `
                    <button class="game-picker-item w-full flex items-center gap-3 p-4 rounded-xl border border-gray-200 dark:border-gray-600 hover:border-fuchsia-400 hover:bg-fuchsia-50 dark:hover:bg-fuchsia-900/20 transition-all text-left" data-game-type="${g.key}" data-match-id="${matchId}">
                        <span class="text-3xl">${g.emoji}</span>
                        <div class="flex-1">
                            <p class="font-semibold text-gray-900 dark:text-white">${Utils.escapeHtml(g.name)}</p>
                            <p class="text-xs text-gray-500">${Utils.escapeHtml(g.desc)}</p>
                            <p class="text-xs text-fuchsia-500 mt-1">${t('games.rounds', { count: g.rounds })}</p>
                        </div>
                        <i class="fas fa-chevron-right text-gray-400"></i>
                    </button>
                `).join('')}
            </div>
            <button id="cancel-game-picker" class="w-full mt-4 py-3 text-gray-500 hover:text-gray-700 text-sm font-medium">
                ${t('common.cancel')}
            </button>
        </div>
    `;

    pickerEl.classList.remove('hidden');
    pushModalState('game-picker-sheet');
    bindPickerEvents(matchId);
}

function bindPickerEvents(matchId) {
    const overlay = document.getElementById('game-picker-overlay');
    const closeBtn = document.getElementById('close-game-picker');
    const cancelBtn = document.getElementById('cancel-game-picker');

    const close = () => closeGamePicker();
    if (overlay) overlay.addEventListener('click', close);
    if (closeBtn) closeBtn.addEventListener('click', close);
    if (cancelBtn) cancelBtn.addEventListener('click', close);

    document.querySelectorAll('.game-picker-item').forEach(btn => {
        btn.addEventListener('click', () => {
            const gameType = btn.dataset.gameType;
            sendGameInviteAction(matchId, gameType);
            close();
        });
    });
}

export function openGamePicker() {
    if (!AppState.currentMatch) return;
    renderGamePicker(AppState.currentMatch.id);
}

export function closeGamePicker() {
    const pickerEl = document.getElementById('game-picker-sheet');
    if (pickerEl) {
        pickerEl.classList.add('hidden');
        pickerEl.innerHTML = '';
        removeModalState('game-picker-sheet');
    }
}

/**
 * Send a game invite via socket or REST fallback
 */
async function sendGameInviteAction(matchId, gameType) {
    if (isConnected()) {
        sendGameInvite(matchId, gameType);
    } else {
        try {
            const result = await GamesAPI.createSession(matchId, gameType);
            if (result.success) {
                // Message will appear via polling or next load
                showToast(t('games.gameStarted'), 'success');
            } else {
                showToast(result.message || t('games.errorGeneric'), 'error');
            }
        } catch (error) {
            showToast(t('games.errorGeneric'), 'error');
        }
    }
}

/**
 * Render a game message in chat (delegates to correct renderer)
 */
export function renderGameMessage(message, isOwn) {
    const gameData = message.game_data;
    if (!gameData) return '';

    const gameType = gameData.gameType;
    const renderer = renderers[gameType];
    if (!renderer) return '';

    const partnerName = window._currentChatPartnerName || '';

    if (message.message_type === 'game_invite') {
        return `<div class="flex justify-center mb-4">${renderer.renderInvite(message, isOwn, partnerName)}</div>`;
    }

    if (message.message_type === 'game_result') {
        return `<div class="flex justify-center mb-4">${renderer.renderResult(gameData.result, partnerName)}</div>`;
    }

    if (message.message_type === 'game_action') {
        // game_action messages (accept/decline) are handled by game_update events
        // Show a minimal system message
        if (gameData.action === 'accepted') {
            return `<div class="flex justify-center mb-4">
                <p class="text-xs text-gray-400 bg-gray-100 dark:bg-gray-700 rounded-full px-3 py-1">${t('games.gameStarted')}</p>
            </div>`;
        }
        return '';
    }

    return '';
}

/**
 * Handle game update events from socket
 */
export function handleGameUpdate(data) {
    const currentUserId = AppState.currentUser?.id;
    if (!currentUserId) return;

    const { sessionId, gameType, status, events, state, result } = data;
    const renderer = renderers[gameType];
    if (!renderer) return;

    const partnerName = window._currentChatPartnerName || '';

    if (status === 'declined') {
        // Update invite card to show declined
        const inviteCard = document.querySelector(`.game-invite-card[data-session-id="${sessionId}"]`);
        if (inviteCard) {
            const btns = inviteCard.querySelectorAll('.game-accept-btn, .game-decline-btn');
            btns.forEach(b => b.remove());
            const statusEl = document.createElement('span');
            statusEl.className = 'text-gray-400 text-sm';
            statusEl.textContent = t('games.declined');
            inviteCard.appendChild(statusEl);
        }
        return;
    }

    if (status === 'active' && state) {
        activeGameSessionId = sessionId;
        // Find existing active card or create one
        let card = document.querySelector(`.game-active-card[data-session-id="${sessionId}"]`);
        const gameState = {
            current_round: data.currentRound,
            total_rounds: data.totalRounds,
            state
        };

        const newHtml = renderer.renderGameCard(sessionId, gameState, currentUserId, partnerName);
        if (!newHtml) return;

        if (card) {
            card.outerHTML = newHtml;
        } else {
            // Add game card to messages container
            const container = document.getElementById('messages-container');
            if (container) {
                container.insertAdjacentHTML('beforeend', `<div class="flex justify-center mb-4">${newHtml}</div>`);
                container.scrollTop = container.scrollHeight;
            }
        }

        // Re-bind events after DOM update
        setTimeout(() => bindGameCardEvents(), 50);
    }

    if (status === 'completed' && result) {
        activeGameSessionId = null;
        // Remove active card if exists
        const card = document.querySelector(`.game-active-card[data-session-id="${sessionId}"]`);
        if (card) {
            const wrapper = card.closest('.flex.justify-center');
            if (wrapper) wrapper.remove();
            else card.remove();
        }
        // Result card will come via new_message event with game_result type
    }
}

/**
 * Handle a game action (accept, decline, answer, choose_type, complete_round)
 */
export async function handleGameAction(sessionId, action, data) {
    if (isConnected()) {
        sendGameAction(sessionId, action, data);
    } else {
        try {
            const result = await GamesAPI.submitAction(sessionId, action, data);
            if (!result.success) {
                showToast(result.message || t('games.errorGeneric'), 'error');
            }
        } catch (error) {
            showToast(t('games.errorGeneric'), 'error');
        }
    }
}

/**
 * Accept or decline a game via socket/REST
 */
async function handleAcceptDecline(sessionId, action) {
    if (isConnected()) {
        sendGameAction(sessionId, action, {});
    } else {
        try {
            const result = action === 'accept'
                ? await GamesAPI.acceptGame(sessionId)
                : await GamesAPI.declineGame(sessionId);
            if (!result.success) {
                showToast(result.message || t('games.errorGeneric'), 'error');
            }
        } catch (error) {
            showToast(t('games.errorGeneric'), 'error');
        }
    }
}

/**
 * Bind click handlers to game cards in the DOM
 */
export function bindGameCardEvents() {
    // Accept buttons
    document.querySelectorAll('.game-accept-btn').forEach(btn => {
        if (btn.dataset.bound) return;
        btn.dataset.bound = 'true';
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const sid = btn.dataset.sessionId;
            handleAcceptDecline(sid, 'accept');
            btn.disabled = true;
            btn.textContent = '...';
        });
    });

    // Decline buttons
    document.querySelectorAll('.game-decline-btn').forEach(btn => {
        if (btn.dataset.bound) return;
        btn.dataset.bound = 'true';
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const sid = btn.dataset.sessionId;
            handleAcceptDecline(sid, 'decline');
            btn.disabled = true;
        });
    });

    // Answer buttons (This or That / Would You Rather)
    document.querySelectorAll('.game-answer-btn').forEach(btn => {
        if (btn.dataset.bound) return;
        btn.dataset.bound = 'true';
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const sid = btn.dataset.sessionId;
            const choice = btn.dataset.choice;

            // Visual feedback
            btn.classList.add('selected', 'border-fuchsia-500', 'bg-fuchsia-50');
            btn.closest('.grid').querySelectorAll('.game-answer-btn').forEach(b => {
                b.disabled = true;
                if (b !== btn) b.classList.add('opacity-40');
            });

            handleGameAction(sid, 'answer', { choice });
        });
    });

    // Truth or Dare choose buttons
    document.querySelectorAll('.game-tod-choose-btn').forEach(btn => {
        if (btn.dataset.bound) return;
        btn.dataset.bound = 'true';
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const sid = btn.dataset.sessionId;
            const choice = btn.dataset.choice;

            btn.classList.add('selected', 'ring-2', 'ring-fuchsia-500');
            btn.closest('.grid').querySelectorAll('.game-tod-choose-btn').forEach(b => {
                b.disabled = true;
                if (b !== btn) b.classList.add('opacity-40');
            });

            handleGameAction(sid, 'choose_type', { choice });
        });
    });

    // Truth or Dare done button
    document.querySelectorAll('.game-tod-done-btn').forEach(btn => {
        if (btn.dataset.bound) return;
        btn.dataset.bound = 'true';
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const sid = btn.dataset.sessionId;
            btn.disabled = true;
            btn.textContent = '✓';
            handleGameAction(sid, 'complete_round', {});
        });
    });
}

/**
 * Setup game-related event listeners
 */
export function setupGameEventListeners() {
    window.addEventListener('socket:game_update', (e) => {
        handleGameUpdate(e.detail);
    });
}
